/**
 * @module app.cd
 */

import { autoinject, observable, bindingMode } from 'aurelia-framework';
import { ComponentBase, IForm, StandardLoadOptions } from 'app/shared/lib/essette-site';
import { I18N } from 'aurelia-i18n';
import { bindable } from 'aurelia-typed-observable-plugin';
import { apiCall } from 'app/shared/lib/api/api';
import { MemberService, IMemberSearchFilters } from 'app/cd/services/member-service';
import { MemberSelect } from 'app/cd/components/member-select'
import { ContactLogPageContext } from 'app/cd/lib/contact-log-page-context';
import { getReference } from 'app/shared/models/essette-types';
import { Toaster } from 'app/shared/lib/toaster';
import { deepClone, forceNull } from 'app/shared/lib/utilities';
import { compareObjects } from 'app/shared/lib/compare-objects';
import { validatePostalCode, validatePhoneNumber } from 'app/shared/lib/validation/validation-utilities';
import { guid } from 'app/shared/lib/guid';
import * as Core from 'app/core/models/essette-api';
import * as CD from 'app/cd/models/essette-api';

@autoinject
export class SaveContact extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////
    @bindable.object({ defaultBindingMode: bindingMode.toView })
    protected contact: CD.MemberContact = {};

    protected originalContact?: CD.MemberContact | null = {};

    @observable
    public selectedMember: CD.Member = {};

    @observable
    public validSelectedMember: boolean = false;

    @bindable({ defaultBindingMode: bindingMode.toView })
    protected contacts: CD.MemberContactComposite[] = [];

    @bindable({ defaultBindingMode: bindingMode.toView })
    public memberId: string | null = null;

    @bindable({ defaultBindingMode: bindingMode.toView })
    protected relationships: ReadonlyArray<CD.MemberRelationship> = [];

    @bindable({ defaultBindingMode: bindingMode.toView })
    protected types: ReadonlyArray<CD.MemberContactType> = [];

    @bindable({ defaultBindingMode: bindingMode.toView })
    protected regions: ReadonlyArray<Core.AddressRegion> = [];

    @bindable.boolean()
    protected showHouseholdFields: boolean = false;

    protected searchOption: string = 'nonMember';
    protected optOutOptions: string[] = [this.i18n.tr('ui:labels.yes'), this.i18n.tr('ui:labels.no')];
    protected selectedOptOut: string | null = null;
    protected selectedTypes: Array<CD.MemberContactType> = [];

    @bindable({ defaultBindingMode: bindingMode.toView })
    protected systemCodes?: { [key: string]: string };

    protected validatePostalCode = validatePostalCode;
    protected validatePhoneNumber = validatePhoneNumber;
    protected addressIncomplete = false;
    @bindable()
    protected managedMember: boolean = false;
    protected editIdentifier: boolean = false;
    protected id: guid = guid();

    ////****************** Element References ***************************************////
    protected memberSelect: MemberSelect | null = null;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly i18n: I18N,
        private readonly memberService: MemberService,
        private readonly context: ContactLogPageContext,
        private readonly toaster: Toaster,
        ...rest: any[]) {
        super(...rest);
    }


    ////****************** Events ***************************************************////
    protected contactChanged(): void {
        // Create original copy of contact for edit tracking
        this.originalContact = deepClone(this.contact);

        // Configure if contact is a "Member"
        if(this.contact?.relatedMember) {
            this.searchOption = 'searchMembers';
            this.selectedMember = deepClone(this.contact.relatedMember);
        }

        this.selectedTypes = this.contact?.types ?? [];

        if(this.contact?.optOut != null) {
            this.selectedOptOut = this.contact?.optOut ? this.optOutOptions[0] : this.optOutOptions[1];
        }

        // check for preexisting address data to mark fields as required
        this.addressChanged();
    }

    protected optionsChoiceChanged(): void {
        this.contact = {};
        this.selectedMember = {};
    }

    protected async selectedMemberChanged(): Promise<void> {
        this.contact.phoneNumber = this.selectedMember?.id && this.selectedMember?.phoneNumber ? deepClone(this.selectedMember.phoneNumber) : {};
        this.contact.address = this.selectedMember?.id && this.selectedMember?.address ? deepClone(this.selectedMember.address) : {};
        this.contact.emailAddress = this.selectedMember?.emailAddress ?? undefined;

        // Logic to determine if selected member is a valid choice
        const isMemberSelected: boolean = !!this.selectedMember?.id;
        const isMemberSelf: boolean = this.selectedMember?.id === this.memberId;
        const isMemberAlreadyRelated: boolean =
            !this.contact?.id && this.contacts ? this.contacts.some(contact => contact.memberContact?.relatedMember?.id === this.selectedMember?.id) : false;
        this.validSelectedMember = isMemberSelected && !isMemberSelf && !isMemberAlreadyRelated;

        if(!this.validSelectedMember && isMemberSelected) {
            this.toaster.warn().message(this.i18n.tr('ui:messages.warnings.memberIsSelfOrRelated')).show();
        }
    }

    protected memberSearchFiltersChanged(newFilters: IMemberSearchFilters, oldFilters: IMemberSearchFilters): void {
        this.context.memberSearchFilters = newFilters;
    }

    protected addressChanged(): void {
        // if any address fields have data, the rest of the address fields need data
        if(!this.contact.address?.mailing) return;
        const { street1, street2, city, region, postalCode } = this.contact.address.mailing;
        this.addressIncomplete = !!street1 || !!street2 || !!city || !!region || !!postalCode;
    }

    ////****************** Functions ************************************************////
    public hasChanges(): boolean {
        throw new Error('Method not implemented.');
    }

    public reset(): void {
        this.searchOption = 'nonMember';
        this.selectedTypes = [];
        this.selectedOptOut = null;
        this.selectedMember = {};
        this.contact = {};
        if(this.memberSelect) this.memberSelect.clear();
        this.editIdentifier = false;
    }

    public submittedChanges?: any;
    public async submit(): Promise<boolean> {
        if(await this.saveContact()) {
            this.submittedChanges = this.contact;
            return true;
        }
        return false;
    }

    private buildMemberContact(): CD.MemberContact | null {
        const returnedContact: CD.MemberContact = {
            id: this.contact?.id,
            member: {
                id: this.memberId
            },
            relationship: getReference(this.contact?.relationship) || null,
            optOut: this.selectedOptOut == this.i18n.tr('ui:labels.yes'),
            types: []
        };

        if(this.selectedTypes.length > 0) {
            this.selectedTypes.forEach(type => {
                const typeReference = getReference(type);
                if(typeReference) {
                    returnedContact.types?.push(typeReference);
                }
            });
        }

        if(this.selectedMember?.id) {
            returnedContact.relatedMember = getReference(this.selectedMember);
        } else {
            returnedContact.name = { full: this.contact?.name?.full };
            returnedContact.emailAddress = forceNull(this.contact?.emailAddress);
            if(this.managedMember) { returnedContact.identifier = { external: this.contact?.identifier?.external } }
        }

        // If no changes, do not create object
        const propertyFilter = (propertyKey: string | number, target: any, propertyPath: string[]) => ["emailAddress", "member", "name", "optOut", "relationship", "types"].includes(propertyKey.toString())
            || (propertyPath.includes('member') && propertyKey === 'id')
            || (propertyPath.includes('name') && propertyKey === 'full')
            || (propertyPath.includes('relationship') && propertyKey === 'id')
            || (propertyPath.includes('types') && propertyKey === 'id');
        const result = compareObjects(this.originalContact, returnedContact, { propertyFilter });
        if(result.areEqual) return null;

        return returnedContact;
    }

    private buildMemberContactAddress(address?: CD.MemberContactAddress | null): CD.MemberContactAddress | null {
        // Return null if no address changes have been made (first condition) or if address is empty and has no ID to deactivate (second condition)
        if(!address || (address && !address.id && !address.street1)) return null;

        let returnedAddress: CD.MemberContactAddress = {
            id: this.contact?.address?.mailing?.id,
            street1: this.contact?.address?.mailing?.street1,
            street2: this.contact?.address?.mailing?.street2,
            city: this.contact?.address?.mailing?.city,
            region: getReference(this.contact?.address?.mailing?.region),
            postalCode: this.contact?.address?.mailing?.postalCode,
            contact: getReference(this.contact)
        };

        // If no changes, do not create object
        const propertyFilter = (propertyKey: string | number, target: any, propertyPath: string[]) => ["street1", "street2", "region", "city", "postalCode"].includes(propertyKey.toString()) || (propertyPath.includes('region') && propertyKey === 'id');
        const result = compareObjects(this.originalContact?.address?.mailing, returnedAddress, { propertyFilter });
        if(result.areEqual) return null;

        // If no ID, add data required for !insert
        if(!returnedAddress.id && this.systemCodes) {
            returnedAddress.type = { id: this.systemCodes['CD.MemberContact.Mailing'] };
        }

        return returnedAddress;
    }

    private buildMemberContactPhoneNumber(phoneNumber?: CD.MemberContactPhoneNumber | null, systemCode?: string): CD.MemberContactPhoneNumber | null {
        // Return null if no phone number changes have been made (first condition) or if phone number is empty and has no ID to deactivate (second condition)
        if(!phoneNumber || !systemCode || (phoneNumber && !phoneNumber.id && !phoneNumber.number)) return null;

        const returnedPhoneNumber: CD.MemberContactPhoneNumber = {
            id: phoneNumber.id,
            number: phoneNumber.number?.replace(/[^0-9]/g, ''), // regex to remove all non-digits
            contact: getReference(this.contact)
        }

        // If no changes, do not create object
        const propertyFilter = (propertyKey: string | number, target: any, propertyPath: string[]) => propertyKey === 'number';
        switch(systemCode) {
            case 'CD.MemberContact.Cell':
                const resultCell = compareObjects(this.originalContact?.phoneNumber?.cell, returnedPhoneNumber, { propertyFilter });
                if(resultCell.areEqual) return null;
                break;
            case 'CD.MemberContact.Home':
                const resultHome = compareObjects(this.originalContact?.phoneNumber?.home, returnedPhoneNumber, { propertyFilter });
                if(resultHome.areEqual) return null;
                break;
            case 'CD.MemberContact.Work':
                const resultWork = compareObjects(this.originalContact?.phoneNumber?.work, returnedPhoneNumber, { propertyFilter });
                if(resultWork.areEqual) return null;
                break;
            case 'CD.MemberContact.Fax':
                const resultFax = compareObjects(this.originalContact?.phoneNumber?.fax, returnedPhoneNumber, { propertyFilter });
                if(resultFax.areEqual) return null;
                break;
            default:
                return null;
        }

        // If no ID, add data required for !insert or !reactivate
        if(!returnedPhoneNumber.id && this.systemCodes) {
            returnedPhoneNumber.type = { id: this.systemCodes[systemCode] };
        }

        return returnedPhoneNumber;
    }

    private async housekeepMemberContactAddress(address: CD.MemberContactAddress): Promise<void> {
        if(!address.id && address.street1) {
            //it could be insert or reactivate
            //query db for inactive address // if exists, reactivate // only then we update
            if(address.contact?.id && address.type?.id) {
                const includes: string[] = [];
                const options: StandardLoadOptions = {
                    includeInactive: true
                };
                const fields = ['id'];
                const addressToReactivate = await this.memberService.loadContactAddressByContact(address.contact.id, address.type.id, options, includes, fields)
                if(addressToReactivate.results.length > 0 && addressToReactivate.results[0].id) {
                    // reactivate
                    await this.memberService.activateMemberContactAddress(addressToReactivate.results[0].id, true)
                    address.id = addressToReactivate.results[0].id;
                }
            }
        }
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async saveContact(): Promise<boolean> {
        if(!this.contact) return false;

        let updateMember = this.contact?.id;

        try {
            this.state.beginSave();

            let isSuccessful = true;
            const memberContact = this.buildMemberContact();
            if(memberContact) {
                const contactResponse = await this.memberService.saveMemberContact(memberContact);
                isSuccessful = contactResponse.isSuccessful;
                this.contact.id = contactResponse.id;
            }

            if(isSuccessful && this.contact.id && this.searchOption === 'nonMember') {
                const promises = [];
                const address = this.buildMemberContactAddress(this.contact?.address?.mailing);
                if(address) {
                    await this.housekeepMemberContactAddress(address);
                    promises.push(await this.memberService.handleMemberContactAddress(address));
                }
                const phoneNumbers = [
                    { number: this.contact?.phoneNumber?.cell, systemCode: 'CD.MemberContact.Cell' },
                    { number: this.contact?.phoneNumber?.home, systemCode: 'CD.MemberContact.Home' },
                    { number: this.contact?.phoneNumber?.work, systemCode: 'CD.MemberContact.Work' },
                    { number: this.contact?.phoneNumber?.fax, systemCode: 'CD.MemberContact.Fax' }
                ];

                let memberContactPhoneNumbers: CD.MemberContactPhoneNumber[] | null = null;
                for(const { number, systemCode } of phoneNumbers) {
                    let phoneNumber = this.buildMemberContactPhoneNumber(number, systemCode);
                    if(phoneNumber) {
                        // if there is no id and there is a number, then look to see if type exists, and if so then reactivate it
                        if(!phoneNumber.id && phoneNumber.number) {
                            // only load inactive phone numbers once per save if needed
                            if(!memberContactPhoneNumbers) memberContactPhoneNumbers = await this.loadInactiveContactPhoneNumbersByContact(this.contact.id);
                            if(memberContactPhoneNumbers) {
                                const phoneNumberToReactivate = memberContactPhoneNumbers.find(memberContactPhoneNumber => memberContactPhoneNumber?.type?.id === phoneNumber?.type?.id);
                                if(phoneNumberToReactivate && phoneNumberToReactivate.id) {
                                    // reactivate
                                    await this.memberService.activateMemberContactPhoneNumber(phoneNumberToReactivate.id, true)
                                    phoneNumber.id = phoneNumberToReactivate.id;
                                }
                            }
                        }
                        promises.push(await this.memberService.handleMemberContactPhoneNumber(phoneNumber));
                    }
                }
                await Promise.all(promises);
            }

            if(this.managedMember) {
                if(updateMember) {
                    this.toaster.standard.updated('cd:memberContact.$', isSuccessful);
                }
                else { this.toaster.standard.created('cd:memberContact.$', isSuccessful); }
            }
            else { this.toaster.standard.saved('cd:memberContact.$', isSuccessful); }

            return isSuccessful;
        }
        catch(error) {
            this.toaster.standard.saved('cd:memberContact.$', false);
            throw error;
        }
        finally {
            this.state.endSave();
        }
    }

    @apiCall
    private async loadInactiveContactPhoneNumbersByContact(contactId: string): Promise<CD.MemberContactPhoneNumber[] | null> {
        const includes: string[] = [
            'type'
        ];

        const options: StandardLoadOptions = {
            includeInactive: true
        };

        const fields = [
            'id',
            'type.id'
        ];

        try {
            this.state.beginLoad();
            const response = await this.memberService.loadInactiveContactPhoneNumbersByContact(contactId, options, includes, fields);
            return response.results;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }
}
